import apiHandler, { api } from '../api/apiHandler';

export interface Contact { name: string; phone?: string; email?: string; }
export interface Hospital { _id: string; name: string; description?: string; logoUrl?: string; defaultContact?: Contact | null; isActive: boolean; }
export interface Benefit { _id: string; name: string; category: string; iconUrl?: string; isActive: boolean; }
export interface Assignment {
  _id: string; branchId: string; benefitId: Benefit; benefitType: 'discount' | 'free'; discountPercent?: number;
  terms: string; validFrom?: string | null; validUntil?: string | null; isActive: boolean;
}
export interface Branch {
  _id: string; hospitalId: Hospital | null; name: string; address: string; city: string; state?: string; pincode?: string;
  latitude?: number | null; longitude?: number | null; phone?: string; images: string[];
  contactPerson?: Contact | null; effectiveContact?: Contact | null;
  eligibility: string; claimInstructions: string; validFrom: string; validUntil: string;
  claimCycle: number; isActive: boolean; benefits: Assignment[]; version: string;
}
export type EntityKind = 'hospitals' | 'branches' | 'benefits' | 'branch-benefits';
interface Page<T> { data: T[]; pagination: { page: number; total: number; totalPages: number }; }

async function request<T>(path: string, method = 'GET', data?: any): Promise<T> {
  const configured = api.defaults.baseURL;
  if (!configured) throw new Error('The API address is not configured.');
  const base = configured.endsWith('/') ? configured : `${configured}/`;
  const url = new URL(`../v2/perks/${path}`, base).toString();
  const response = await apiHandler(url, method, data);
  if (response.error || response.status < 200 || response.status >= 300) throw new Error(response.message || 'Unable to load or save perks.');
  return response.data;
}

export const perksV2 = {
  list: <T,>(kind: EntityKind, page = 1, search = '', expired: '' | 'true' | 'false' = '', hospitalId = '') =>
    request<Page<T>>(`${kind}?page=${page}&limit=20&search=${encodeURIComponent(search)}${kind === 'branches' && expired ? `&expired=${expired}` : ''}${kind === 'branches' && hospitalId ? `&hospitalId=${encodeURIComponent(hospitalId)}` : ''}`),
  branch: (id: string) => request<{ data: Branch }>(`branches/${id}`),
  save: (kind: EntityKind, id: string | undefined, input: any) => request(`${kind}${id ? `/${id}` : ''}`, id ? 'PUT' : 'POST', input),
  remove: (kind: EntityKind, id: string) => request(`${kind}/${id}`, 'DELETE'),
  all: async <T,>(kind: 'hospitals' | 'benefits', isActive?: boolean): Promise<T[]> => {
    const items: T[] = [];
    for (let page = 1; ; page++) {
      const result = await request<Page<T>>(`${kind}?page=${page}&limit=100${isActive === undefined ? '' : `&isActive=${isActive}`}`);
      items.push(...result.data);
      if (page >= result.pagination.totalPages) return items;
    }
  },
};
